// ============================================================================
// GsDrawer — tiroir latéral droit générique (@gs/ui)
// ============================================================================
// MASTER-UG-REDESIGN-F2-MEMBRES 2026-06-22 : scrim, header/body scrollable,
// footer slot, Échap, focus-trap basique. Tokens slate + w-console-drawer.
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { GS_COMPONENTS } from '../tokens';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface GsDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Label accessible si title n'est pas une chaîne. */
  ariaLabel?: string;
  closeLabel?: string;
}

export function GsDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  ariaLabel,
  closeLabel = 'Fermer',
}: GsDrawerProps) {
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const timer = window.setTimeout(() => {
      const el = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      el?.focus();
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-slate-900/30 motion-reduce:transition-none"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        className="fixed bottom-0 right-0 top-0 z-50 flex w-console-drawer max-w-[92vw] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      >
        {(title || subtitle) ? (
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-slate-200 px-4 py-3.5 dark:border-slate-700">
            <div className="min-w-0">
              {title ? (
                <span className="block text-body font-bold text-slate-900 dark:text-slate-50">{title}</span>
              ) : null}
              {subtitle ? (
                <span className="mt-0.5 block text-meta text-slate-500 dark:text-slate-400">{subtitle}</span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className={GS_COMPONENTS.btnClose}
            >
              <X size={15} aria-hidden />
            </button>
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 py-3.5">{children}</div>
        {footer ? (
          <div className="flex shrink-0 items-center gap-2.5 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
            {footer}
          </div>
        ) : null}
      </aside>
    </>
  );
}
